<template>

  <v-container class="d-flex flex-column">
    <div class="form align-self-center py-sm-10">
      <h2 class="text-h5 font-weight-bold">Welcome to GUIOMENTOR</h2>
      <h5 class="subtitle-1">Let's start with a few simple steps</h5>
      <div class="mt-5">
        <v-form class="my-1 py-0">
          <v-progress-linear v-if="loading" indeterminate />
          <v-text-field
            v-model="email"
            :error-messages="emailErrors"
            :loading="emailLoading"
            class="my-7 py-0"
            label="Email"
            @input="emailOnChange()"
          />
          <v-select
            v-model.trim="accountType"
            :error-messages="accountTypeMessages"
            :items="accountTypes"
            label="What do you do ?"
            class="accountType my-1 py-0"
            data-test="accountType"
            mx-20
            required
            @blur="$v.accountType.$touch()"
            @input="$v.accountType.$touch()"
          />
          <v-text-field
            v-model="password"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="[...passwordErrors, ...passwordStrengthErrors]"
            :type="showPassword ? 'text' : 'password'"
            class="my-1 py-0"
            label="Password"
            @click:append="showPassword = !showPassword"
          />
          {{passwordStrength}}
        <password  @score="showScore"
            @feedback="showFeedback" v-model="password" :strength-meter-only="true"/>

          <v-text-field
            v-model="confirmPassword"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="confirmPasswordErrors"
            :type="showPassword ? 'text' : 'password'"
            class="my-1 py-0"
            label="Confirm Password"
            @click:append="showPassword = !showPassword"
          />
        </v-form>
      </div>
      <v-row class="my-0 mt-1">
        <v-col class="d-flex flex-row mx-0 my-0 px-0 py-0">
           <v-checkbox
            v-model="agreeToTerms"
            :label="termsAndPrivacy"
            :value="agreeToTerms"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row class="my-0 mt-1">
        <v-col class="d-flex flex-row mx-0 my-0 px-0 py-0">
          <v-btn
            :disabled="$v.$invalid || loading || !!emailTaken"
            :loading="loading"
            block
            color="primary"
            @click="createAccount()"
          >
            Create Account
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="mt-2 px-0">
        <v-col
          class="
            d-flex
            flex-md-row
            justify-space-between
            flex-grow-1
            mx-0
            px-0
            my-0
          "
        >
          <v-btn color="primary" outlined to="/login" width="46%">
            Log me in
          </v-btn>
          <v-btn class="ml-auto" text to="/forgot_password" width="46%"
            >Forgot Password</v-btn
          >
        </v-col>
      </v-row>
      <v-row class="mx-0 px-0">
        <v-col class="d-flex flex-column align-stretch mx-0 px-0" cols="12">
          <p class="subtitle-1">Sign up with social,</p>
          <GoogleSignInButton />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import google from "@/assets/google.png";
import Logo from "@/assets/guiomentorLogo.png";
import { email, minLength, required, sameAs } from "vuelidate/lib/validators";
import { mapMutations } from "vuex";
import GoogleSignInButton from "../../components/GoogleSignInButton";
  import Password from 'vue-password-strength-meter'

export default {
  components: {GoogleSignInButton, Password},
  mounted() {
    const userEmail = this.$router.currentRoute.params.email
    if (userEmail) {
      this.email = userEmail
      this.accountType = "MENTOR"
    }
    const accountType = this.$router?.history?.current?.query?.type || "";
    if (accountType) {
      this.accountType = accountType.toUpperCase();
    }
  },
  data() {
    return {
      google,
      Logo,
      email: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      loading: false,
      emailTaken: false,
      emailLoading: false,
      accountTypes: ["STUDENT", "MENTOR"],
      accountType: "",
      passwordStrengthErrors: [],
      passwordScore: 0,
      description:
        "Guiomentor is a platform directed to a niche of 10 to 25 years old. Its objective is to help students to choose the best career or discipline for them according to their skills and part of interest ",
      agreeToTerms: false,
      termsAndPrivacy: 'By clicking "Create account", you agree to the Terms of use and the Privacy Policy of Guiomentor'
    };
  },
  metaInfo() {
    return {
      title: "Guiomentor - Sign up",
      url: window.location.href,
      meta: [
        { name: "title", content: "Guiomentor - Create Account with us" },
        { name: "description", content: this.description },
        //Facebook
        { name: "og:type", content: "Website" },
        { name: "og:title", content: "Guiomentor - Create an Account with us" },
        { name: "og:description", content: this.description },
        { name: "og:image", content: this.Logo },

        // Twitter
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:url", content: window.location.href },
        {
          name: "twitter:title",
          content: "Guiomentor - Create an Account with us",
        },
        { name: "twitter:description", content: this.description },
        { name: "twitter:image", content: this.Logo },
      ],
    };
  },
  methods: {
    ...mapMutations({
      SET_AUTH_SUCCESS: "auth/SET_AUTH_SUCCESS",
      setProfile: "auth/SET_USER_PROFILE",
    }),
    emailOnChange() {
      this.$v.email.$touch();
      this.emailTaken = false;
    },
    async createAccount() {
      try {
        this.loading = true;
        const { email, password, accountType } = this;
        const body = {
          email,
          account_types: [{ name: accountType }],
          password,
        };
        const { data } = await this.axios.post("users/create/", body);
        this.SET_AUTH_SUCCESS({
          ...data,
          profile: data.profile,
        });
        this.setProfile(data.profile);
        localStorage.setItem("access_token", data.tokens.access);
        localStorage.setItem("refresh_token", data.tokens.refresh);
        this.$snackbar.showMessage({
          content: this.$t("auth.signup.signupSuccessMessage"),
          color: "success",
        });
        this.$analytics.logEvent("user_sign_up", {
          user: data,
          time: new Date(),
          url: window.location.href,
          version: process.env.VUE_APP_VERSION,
          environment: process.env.VUE_APP_ENVIRONMENT,
        });
        await this.$router.push("/on-boarding");
      } catch (error) {
        this.$snackbar.showMessage({
          content:
            error.response?.data?.detail ||
            this.$t("errors.somethingWentWrongError"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
     showFeedback ({suggestions}) {
      this.passwordStrengthErrors = [...suggestions];
      },
      showScore (score) {
        this.passwordScore = score;
      }
  },
  computed: {
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email &&
        errors.push(this.$t("auth.forms.errors.emailMustBeValid"));
      !this.$v.email.required &&
        errors.push(this.$t("auth.forms.errors.emailRequired"));
      this.emailTaken &&
        errors.push(this.$t("errors.emailTaken", { email: this.email }));
      return errors;
    },
    accountTypeMessages() {
      const errors = [];
      if (!this.$v.accountType.$dirty) return errors;
      !this.$v.accountType.min &&
        errors.push(this.$t("auth.forms.errors.accountType"));
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      !this.$v.password.required &&
        errors.push(this.$t("auth.forms.errors.passwordRequired"));
      !this.$v.password.min &&
        errors.push(this.$t("auth.forms.errors.passwordLength"));
      return errors;
    },
  passwordStrength() {
    if(!this.password) return '';
    if(this.passwordScore <= 0) {
        return 'Weak'
    }

    if(this.passwordScore <= 2) {
        return 'Medium'
    }

    return 'Strong'
    },
    confirmPasswordErrors() {
      const errors = [];
      if (!this.$v.confirmPassword.$dirty) return errors;
      !this.$v.confirmPassword.required &&
        errors.push(this.$t("auth.forms.errors.confirmPasswordRequired"));
      !this.$v.confirmPassword.sameAsPassword &&
        errors.push(this.$t("auth.forms.errors.confirmPasswordMatch"));
      return errors;
    },
  },
  validations: {
    email: { required, email },
    password: { required, min: minLength(6) },
    confirmPassword: {
      required,
      min: minLength(6),
      sameAsPassword: sameAs("password"),
    },
    accountType: {
      required,
      min: minLength(1),
    },
    agreeToTerms: {
      required
    }
  },
  watch: {
    async email() {
      if (!this.$v?.email?.$invalid) {
        const { email } = this;
        this.emailLoading = true;
        try {
          await this.axios.post("users/existing_users/", { email });
          this.emailTaken = false;
        } catch (error) {
          if (error?.response?.status === 409) {
            this.emailTaken = true;
          }
        } finally {
          this.emailLoading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables";

.signup-container {
  .row {
    margin: 0;
  }
}

.left {
  background: #0033AA;
  margin-top: 0;
  margin-bottom: 0;

  .logo {
    width: 130px;
    height: 130px;
  }

  .mini-column {
    elevation: higher;
    background: linear-gradient(to right, #b7edeb, #54ded2);
    min-height: 70%;
    max-width: 95%;
    border-radius: 5px;
  }
}

.right {
  background: #f5f6f8;
}

.form {
  background: #ffffff;
  min-height: 450px;
  width: 85%;
  border-radius: 10px;
  padding: 35px;
  box-shadow: 0px 42px 100px rgba(0, 0, 0, 0.12);

  .v-btn {
    text-transform: none !important;
  }

  .social-img {
    width: 20px;
    height: 80%;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .form {
    width: 100%;
  }
}
</style>
